<template>
  <v-card class="elevation-0">
    <v-toolbar flat color="white">
      <v-icon color="success" class="pr-2">description</v-icon>
      <v-toolbar-title class="text-capitalize">Files</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        :disabled="loading"
        @click="$emit('getBlobList')"
        class="ml-auto ma-2"
        small
        outlined
      >
        <v-icon class="pr-2" small>refresh</v-icon>
        Refresh Table
      </v-btn>
    </v-toolbar>

    <v-toolbar flat color="white">
      <v-text-field
        :disabled="loading"
        v-model="search"
        label="Filter files by name, tube or status..."
        single-line
        hide-details
      ></v-text-field>
    </v-toolbar>

    <v-data-table
      :footer-props="footerProps"
      :headers="headers"
      :items="data"
      :loading="loading"
      :search="search"
      item-key="id"
    >
      <template v-slot:item.lastmodified="{ item }">
        <v-tooltip
          v-if="item.processedstatus === 'successful'"
          color="success"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="success"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small left color="white">check</v-icon>
              {{ item.lastmodified | date }}
            </v-chip>
          </template>

          File has been processed.
        </v-tooltip>

        <v-tooltip
          v-else-if="item.processedstatus === 'failed'"
          color="red"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="red"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small left color="white">close</v-icon>
              {{ item.lastmodified | date }}
            </v-chip>
          </template>

          {{ item.processedstatusdetails }}
        </v-tooltip>

        <v-tooltip
          v-else-if="withinLastHour(item.lastmodified)"
          color="blue"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="blue"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-progress-circular
                class="mr-2"
                left
                indeterminate
                color="white"
                :width="2"
                :size="12"
              ></v-progress-circular>

              {{ item.lastmodified | date }}
            </v-chip>
          </template>

          File is currently or awaiting to be processed.
        </v-tooltip>

        <v-tooltip v-else color="grey" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="grey"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small left color="white">not_listed_location</v-icon>
              {{ item.lastmodified | date }}
            </v-chip>
          </template>

          File has not been processed within the last hour.<br />
          Please reprocess the file, or send in another version.
        </v-tooltip>
      </template>

      <template v-slot:item.datasource="{ item }">
        <v-chip color="secondary" text-color="white" light small
          >{{ item.datasource }}
        </v-chip>
      </template>

      <template v-slot:item.totalrecords="{ item }">
        <v-chip :color="totalStyle(item)" light outlined small
          >{{ item.totalrecords || '?' }}
        </v-chip>
      </template>

      <template v-slot:item.filteredrecords="{ item }">
        <v-chip :color="filteredStyle(item)" light outlined small
          >{{ item.filteredrecords || '?' }}
        </v-chip>
      </template>

      <template v-slot:item.processedrecords="{ item }">
        <v-tooltip :color="processedStyle(item)" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              :color="processedStyle(item)"
              light
              outlined
              small
              v-bind="attrs"
              v-on="on"
              >{{ item.processedrecords || '?' }}
            </v-chip>
          </template>
          <v-chip
            class="mx-1"
            :color="processedStyle(item)"
            text-color="white"
            small
          >
            <v-icon small left color="white">save</v-icon>
            {{ item.insertedcount || '?' }}
          </v-chip>
          <v-chip
            class="mx-1"
            :color="processedStyle(item)"
            text-color="white"
            small
          >
            <v-icon small left color="white">edit</v-icon>
            {{ item.updatedcount || '?' }}
          </v-chip>
          <v-chip
            class="mx-1"
            :color="processedStyle(item)"
            text-color="white"
            small
          >
            <v-icon small left color="white">remove_circle</v-icon>
            {{ item.deletedcount || '?' }}
          </v-chip>
        </v-tooltip>
      </template>

      <template v-slot:item.size="{ item }">
        <v-chip color="secondary" light outlined small
          >{{ item.size | sizeMb }}
        </v-chip>
      </template>

      <template v-slot:item.filename="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div class="one-line" v-bind="attrs" v-on="on">
              {{ item.filename }}
            </div>
          </template>

          {{ item.filename }}
        </v-tooltip>
      </template>

      <template v-slot:item.link="{ item }">
        <v-tooltip color="secondary" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :disabled="loading || $store.state.getBlobFile"
              :loading="downloading(item)"
              @click.prevent="download(item)"
              elevation="0"
              v-bind="attrs"
              v-on="on"
              icon
            >
              <v-icon medium color="secondary">cloud_download</v-icon>
            </v-btn>
          </template>
          <v-chip class="mx-1" color="secondary" text-color="white" small>
            <v-icon small left color="white">cloud</v-icon>
            {{ item.size | sizeMb }}
          </v-chip>
        </v-tooltip>
      </template>

      <template v-slot:no-data>
        <v-alert :color="alertMessage.type" dark class="mt-4">
          {{ alertMessage.message }}
        </v-alert>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
export default {
  components: {},
  props: {
    data: { type: Array },
    loading: { type: Boolean },
    client: { type: String },
    resourceType: { type: String },
    wrongDatasource: { type: Boolean },
  },
  methods: {
    download(item) {
      const index = this.data.indexOf(item);
      this.$store.dispatch('getBlobFile', {
        blobname: item.blobname,
        filename: item.filename,
        client: this.client,
        resourceType: this.resourceType,
        index,
      });
    },
    downloading(item) {
      const index = this.data.indexOf(item);
      return this.$store.state.gettingBlobFile.includes(index);
    },
    withinLastHour(date) {
      return new Date() - new Date(date) < 1000 * 60 * 60;
    },
    totalStyle(item) {
      if (typeof item.totalrecords === 'undefined') {
        return 'grey';
      }

      return 'black';
    },
    filteredStyle(item) {
      if (typeof item.filteredrecords === 'undefined') {
        return 'grey';
      }

      const filteredrecords = parseInt(item.filteredrecords);
      const totalrecords = parseInt(item.totalrecords);

      if (filteredrecords === totalrecords) {
        return 'red';
      }

      if (filteredrecords >= totalrecords / 2) {
        return 'orange';
      }

      return 'black';
    },
    processedStyle(item) {
      if (typeof item.processedrecords === 'undefined') {
        return 'grey';
      }

      const processedrecords = parseInt(item.processedrecords);
      const totalrecords = parseInt(item.totalrecords);

      if (processedrecords === 0) {
        return 'red';
      }

      if (processedrecords < totalrecords / 2) {
        return 'orange';
      }

      return 'black';
    },
    sizeWarn(item) {
      return item.size && parseInt(item.size) > 30 * Math.pow(1024, 2);
    },
  },
  computed: {
    headers() {
      if (this.wrongDatasource) {
        return [];
      }

      return [
        {
          text: 'Tube',
          align: 'left',
          width: '180px',
          filterable: true,
          sortable: true,
          value: 'datasource',
        },
        {
          text: 'Status',
          align: 'left',
          width: '200px',
          filterable: true,
          sortable: true,
          value: 'lastmodified',
        },
        {
          text: 'Processed Status',
          filterable: true,
          sortable: false,
          align: ' d-none',
          value: 'processedstatus',
        },
        {
          text: 'File name',
          filterable: true,
          sortable: true,
          value: 'filename',
        },
        {
          text: 'Records',
          align: 'center',
          width: '110px',
          filterable: false,
          sortable: false,
          value: 'totalrecords',
        },
        {
          text: 'Filtered',
          align: 'center',
          width: '110px',
          filterable: false,
          sortable: false,
          value: 'filteredrecords',
        },
        {
          text: 'Processed',
          align: 'center',
          width: '110px',
          filterable: false,
          sortable: false,
          value: 'processedrecords',
        },
        {
          text: '',
          align: 'center',
          width: '100px',
          filterable: false,
          sortable: false,
          value: 'link',
        },
      ];
    },
    alertMessage() {
      if (this.wrongDatasource) {
        return {
          message: 'No scheduled tubes',
          type: 'black',
        };
      }
      return {
        message: 'No processed files within the last month',
        type: 'black',
      };
    },
  },
  data() {
    return {
      expanded: [],
      search: '',
      footerProps: {
        'items-per-page-options': [10, 25, 50, 100],
      },
    };
  },
  filters: {
    sizeMb(value) {
      if (typeof value !== 'string') {
        return '?';
      }

      const sizeInBytes = parseInt(value);

      if (sizeInBytes < 1024) {
        return `${sizeInBytes} B`;
      }

      if (sizeInBytes < Math.pow(1024, 2)) {
        const sizeInKB = (sizeInBytes / 1024).toPrecision(3);

        return `${sizeInKB} KB`;
      }

      if (sizeInBytes < Math.pow(1024, 3)) {
        const sizeInMB = (sizeInBytes / Math.pow(1024, 2)).toPrecision(3);

        return `${sizeInMB} MB`;
      }

      const sizeInGB = (sizeInBytes / Math.pow(1024, 3)).toPrecision(3);
      return `${sizeInGB} MB`;
    },
  },
};
</script>
